import { useCallback, useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router";
import { signIn, submitNewPassword } from "supertokens-web-js/recipe/emailpassword";

import { AuthenticationPage } from "@/components/AuthenticationPage/AuthenticationPage";
import { IconButton } from "@/components/Button/IconButton";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { Form } from "@/components/Form/Form";
import { FormInput } from "@/components/Form/FormInput";
import { EyeIcon } from "@/components/Icons/EyeIcon";
import { EyeOffIcon } from "@/components/Icons/EyeOffIcon";
import { XCloseIcon } from "@/components/Icons/XCloseIcon";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { Stack } from "@/design-system/Stack";
import { removeFromHeaders } from "@/helpers/headers";
import { isHttpError } from "@/helpers/Network/errors";
import { createRequiredStringRule } from "@/helpers/rules";
import { useBool } from "@/hooks/useBool";
import { useQueryParam } from "@/hooks/useQueryParam";
import { usePublicAppBranding } from "@/providers/PublicAppBrandingProvider";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { PasswordHelper } from "../components/PasswordHelper";

enum PageState {
  Initial,
  Failed,
  Success,
  SuccessAndLogin,
}

export function ResetPasswordPage(): React.ReactNode {
  const showFlashToast = useFlashToast();
  const { i18n, t } = useTranslation();
  const { appBranding } = usePublicAppBranding();

  const [globalError, setGlobalError] = useState<string>();
  const [isSubmitting, isSubmittingHandlers] = useBool(false);
  const [state, setState] = useState(PageState.Initial);

  const [showPassword, showPasswordHandlers] = useBool();

  const [token] = useQueryParam("token");
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      void navigate("/");
    }
  }, [navigate, token]);

  const onLogin = useCallback(
    async (password: string) => {
      try {
        const email = new URLSearchParams(window.location.search).get("email");
        if (!email) {
          return false;
        }

        const response = await signIn({
          formFields: [
            {
              id: "email",
              value: email,
            },
            {
              id: "password",
              value: password,
            },
          ],
          options: {
            preAPIHook: (x) => {
              const url = new URL(x.url);
              url.searchParams.set("languageId", i18n.language);
              url.searchParams.set("brand", appBranding);

              return Promise.resolve({
                ...x,
                url: url.toString(),
                requestInit: removeFromHeaders(x.requestInit, "Authorization"),
              });
            },
          },
        });

        return response.status === "OK";
      } catch (error) {
        return false;
      }
    },
    [appBranding, i18n.language],
  );

  const onSubmit = useCallback(
    async (password: string) => {
      isSubmittingHandlers.setTrue();
      try {
        const response = await submitNewPassword({
          formFields: [
            {
              id: "password",
              value: password,
            },
          ],
        });

        if (response.status === "FIELD_ERROR") {
          // This should not happen, we have form validation
          console.error(response.formFields);
          showFlashToast({ variant: "alert", message: t("common.generic-error") });
        } else if (response.status === "RESET_PASSWORD_INVALID_TOKEN_ERROR") {
          setState(PageState.Failed);
        } else {
          const loggedIn = await onLogin(password);
          if (loggedIn) {
            setState(PageState.SuccessAndLogin);
          } else {
            setState(PageState.Success);
          }
        }
      } catch (err: any) {
        if (err.isSuperTokensGeneralError === true) {
          setGlobalError(err.message);
        } else if (isHttpError(err)) {
          setGlobalError(t("page.login.errors.unknown"));
        } else {
          showFlashToast({ variant: "alert", message: t("common.generic-error") });
        }
      } finally {
        isSubmittingHandlers.setFalse();
      }
    },
    [isSubmittingHandlers, onLogin, showFlashToast, t],
  );

  const formMethods = useForm<FormValues>({
    defaultValues: {
      password: "",
    },
  });

  const password = useWatch({ control: formMethods.control, name: "password" });

  const submitCount = formMethods.formState.submitCount;

  let header = t("page.reset-password.title");
  if (state === PageState.Success || state === PageState.SuccessAndLogin) {
    header = t("page.reset-password.completed.title");
  } else if (state === PageState.Failed) {
    header = t("page.reset-password.failed.title");
  }

  let body: string | undefined;
  if (state === PageState.Success) {
    body = t("page.reset-password.completed.description");
  } else if (state === PageState.SuccessAndLogin) {
    body = t("page.reset-password.completed-and-logged-in.description");
  } else if (state === PageState.Failed) {
    body = t("page.reset-password.failed.description");
  }

  return (
    <AuthenticationPage header={header} body={body}>
      {state === PageState.Initial && (
        <Form.Root formMethods={formMethods} onSubmit={(data) => onSubmit(data.password)}>
          <Stack gap="xl">
            {globalError && (
              <aside
                className="relative flex items-center rounded-sm bg-background-danger-secondary p-space-sm text-center text-caption text-text-danger-secondary"
                role="alert"
              >
                <span className="flex-1 px-space-xl">{globalError}</span>
                <span className="absolute right-0">
                  <IconButton
                    title={t("common.action.close")}
                    variant="ghostSecondary"
                    onClick={() => setGlobalError(undefined)}
                    icon={<XCloseIcon />}
                  />
                </span>
              </aside>
            )}
            <FormField label={t("page.login.form.password.label")}>
              <FormInput<FormValues>
                id="password"
                type={showPassword ? "text" : "password"}
                name="password"
                hideErrorText
                autoComplete="current-password"
                autoCapitalize="none"
                spellCheck="false"
                autoFocus
                aria-required
                rules={{
                  validate: {
                    required: createRequiredStringRule(t, "page.login.form.password.label"),
                    minCharacters(password) {
                      if (password.length < 8) {
                        return t("page.login.errors.password-minimum-chars", { chars: 8 });
                      }
                    },
                    containsLowercase(password) {
                      if (password.toUpperCase() === password) {
                        return t("page.login.errors.password-lowercase");
                      }
                    },
                    containsNumber(password) {
                      if (!/\d/.test(password)) {
                        return t("page.login.errors.password-number");
                      }
                    },
                  },
                }}
                postfix={
                  <IconButton
                    title={
                      showPassword
                        ? t("page.login.form.password.toggle-hidden")
                        : t("page.login.form.password.toggle-visible")
                    }
                    onClick={showPasswordHandlers.toggle}
                    variant="ghostSecondary"
                    icon={showPassword ? <EyeOffIcon /> : <EyeIcon />}
                  />
                }
              />

              <PasswordHelper {...{ password, hasSubmittedPassword: submitCount > 0 }} />
            </FormField>
            <Button type="submit" fullWidth isLoading={isSubmitting}>
              {t("page.reset-password.confirm")}
            </Button>
          </Stack>
          <footer className="mt-space-md">
            <Button size="sm" variant="ghostPrimary" type="link" href={routes.authentication.login()}>
              {t("page.reset-password.cancel")}
            </Button>
          </footer>
        </Form.Root>
      )}
      {(state === PageState.Success || state === PageState.SuccessAndLogin || state === PageState.Failed) && (
        <Stack gap="md">
          <Button type="link" href={routes.authentication.login()} fullWidth data-testid="redirect-home-btn">
            {t("page.reset-password.completed.go-home")}
          </Button>
        </Stack>
      )}
    </AuthenticationPage>
  );
}

interface FormValues {
  password: string;
}
